import type { NavigateFunction } from "react-router-dom";
import axiosInstance from "@/lib/axios";
import { automationEndpoint } from "@/config/endpoints";
import type { AppDispatch } from "@/stores/store";
import {
  beginAutomationDraft,
  parseConditionPayload,
  populateFormFromAutomation,
  setAutomationActions,
  setAutomationDescription,
  setAutomationFrequency,
  setAutomationName,
  setAutomationPeriodSeconds,
  setAutomationPriority,
  setAutomationScope,
  setAutomationTrigger,
  setAutomationTriggerConfig,
  setBranchId,
  setConditionTree,
  setRunWindowDays,
  setRunWindowEnd,
  setRunWindowMode,
  setRunWindowStart,
  setStopOnFailure,
} from "@/features/automation/stores/automationFunctionalSlice";
import type { AIToolCallRecord } from "../types/ai.types";

// ---------------------------------------------------------------------------
// Xale AI → automation builder. The server's `draft_automation` tool returns
// a VALIDATED, UNSAVED rule (`action: "automation_draft"`); this lands it in
// the builder's Redux form section by section, so the user watches the rule
// get built and then reviews + presses Save themselves. Nothing here saves.
//
// Per-action settings arrive as `settings` (the server's result envelope
// drops any key named `config`) and are mapped back to the form's `config`.
// ---------------------------------------------------------------------------

export const AUTOMATION_ROUTE = "/automation";
const STEP_MS = 320;

export interface AutomationDraft {
  automationId?: number;
  name: string;
  description?: string;
  scope?: "GLOBAL" | "SPECIFIC";
  branchId?: number | null;
  frequency?: "EVERY_TIME" | "ONCE" | "ONCE_IN_PERIOD";
  periodSeconds?: number | null;
  runWindow?: {
    mode: "ALL" | "BUSINESS_HOURS" | "OUTSIDE_BUSINESS_HOURS";
    days?: number[];
    startMinute?: number;
    endMinute?: number;
  };
  priority?: number;
  stopOnFailure?: boolean;
  trigger: { event: string; label?: string; settings?: Record<string, any> };
  conditions?: unknown;
  actions: { type: string; label?: string; settings?: Record<string, any> }[];
}

export function draftOf(t: AIToolCallRecord): AutomationDraft | null {
  const r = t.result;
  if (t.name !== "draft_automation" || !r || typeof r !== "object") return null;
  if (r.action !== "automation_draft" || !r.draft?.trigger?.event) return null;
  if (!Array.isArray(r.draft.actions)) return null;
  return r.draft as AutomationDraft;
}

// A newer draft (the user asked for a change mid-build) supersedes the one
// still being typed in.
let runToken = 0;
const pause = () => new Promise<void>((r) => setTimeout(r, STEP_MS));

export async function applyAutomationDraft(
  draft: AutomationDraft,
  dispatch: AppDispatch,
  navigate: NavigateFunction
): Promise<void> {
  const token = ++runToken;
  const live = () => token === runToken;

  // Editing: hydrate the real rule first so what the draft does not carry
  // (group, skipIfAlreadyApplied) is saved back unchanged.
  let keepForm = false;
  if (draft.automationId) {
    try {
      const res = await axiosInstance.get(
        `${automationEndpoint}/${draft.automationId}`
      );
      if (!live()) return;
      if (res.data?.automation) {
        dispatch(populateFormFromAutomation(res.data.automation));
        keepForm = true;
      }
    } catch {
      // Fall through: the draft opens as a NEW rule rather than not at all.
    }
  }
  if (!live()) return;
  dispatch(beginAutomationDraft({ keepForm }));
  navigate(AUTOMATION_ROUTE);

  const steps: Array<() => void> = [
    () => {
      dispatch(setAutomationName(draft.name));
      dispatch(setAutomationDescription(draft.description ?? ""));
    },
    () => {
      dispatch(setAutomationTrigger(draft.trigger.event));
      dispatch(setAutomationTriggerConfig(draft.trigger.settings ?? {}));
    },
    () => dispatch(setConditionTree(parseConditionPayload(draft.conditions))),
    // Actions land one at a time, in execution order.
    ...draft.actions.map((_, i) => () => {
      dispatch(
        setAutomationActions(
          draft.actions
            .slice(0, i + 1)
            .map((a) => ({ actionType: a.type, config: a.settings ?? {} }))
        )
      );
    }),
    () => {
      dispatch(setAutomationScope(draft.scope ?? "GLOBAL"));
      dispatch(setBranchId(draft.scope === "SPECIFIC" ? draft.branchId ?? null : null));
      dispatch(setAutomationFrequency(draft.frequency ?? "EVERY_TIME"));
      if (draft.frequency === "ONCE_IN_PERIOD" && draft.periodSeconds) {
        dispatch(setAutomationPeriodSeconds(draft.periodSeconds));
      }
      const rw = draft.runWindow;
      dispatch(setRunWindowMode(rw?.mode ?? "ALL"));
      if (rw && rw.mode !== "ALL") {
        if (rw.days?.length) dispatch(setRunWindowDays(rw.days));
        if (typeof rw.startMinute === "number") dispatch(setRunWindowStart(rw.startMinute));
        if (typeof rw.endMinute === "number") dispatch(setRunWindowEnd(rw.endMinute));
      }
      dispatch(setAutomationPriority(draft.priority ?? 10));
      dispatch(setStopOnFailure(draft.stopOnFailure !== false));
    },
  ];

  for (const step of steps) {
    await pause();
    if (!live()) return;
    step();
  }
}
